import React, { useState } from "react";
import { BACKEND_URL } from "../lib/api";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { MessageCircle, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Normalise an Indian-format phone number into the digits-only format
 * wa.me requires: international code without "+" or spaces.
 *
 *  +91 98765 43210 → 919876543210
 *  9876543210      → 919876543210  (assume +91 if 10 digits)
 *  91-9876543210   → 919876543210
 *  +1 415 555 1234 → 14155551234
 */
export function normaliseWhatsAppPhone(raw) {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (!digits || digits.length < 7) return "";          // too short — treat as empty
    if (digits.length === 10) return `91${digits}`;        // Indian 10-digit
    if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
    return digits;                                         // already has country code
}

/**
 * Build the WhatsApp click-to-chat URL.
 *  - Empty phone falls back to a "pick a contact" share URL.
 *  - Message is URL-encoded.
 */
export function buildWhatsAppLink({ phone, message }) {
    const digits = normaliseWhatsAppPhone(phone);
    const text = encodeURIComponent(message || "");
    if (digits) return `https://wa.me/${digits}?text=${text}`;
    return `https://wa.me/?text=${text}`;
}

/**
 * Build the default invoice message.
 *
 *  Hi {customerName} 👋
 *
 *  Here is your invoice {number} for {amount} from Hahnemann Pharmacy & Laboratory.
 *  Tap the link to view / download:
 *  {publicPdfUrl}
 *
 *  Please reach us at {supportPhone or email} for any questions.
 *  — Hahnemann Pharmacy & Laboratory
 */
export function defaultInvoiceMessage({ inv, companyName = "Hahnemann Pharmacy & Laboratory", support = "" }) {
    const customer = inv?.customer?.name || "there";
    const number = inv?.invoice_number || inv?.id || "";
    const amount = inv?.total != null
        ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(inv.total)
        : "";
    const pdfUrl = `${BACKEND_URL}/api/invoices/public/${inv?.id || ""}/pdf`;
    return [
        `Hi ${customer},`,
        ``,
        `Here is your invoice ${number}${amount ? ` for ${amount}` : ""} from ${companyName}.`,
        `View / download the PDF:`,
        pdfUrl,
        ``,
        support ? `For any questions, reach us at ${support}.` : "",
        `— ${companyName}`,
    ].filter(Boolean).join("\n");
}

/**
 * Inline / icon button that opens a small dialog where the sender can:
 *   • see / edit the customer phone (pre-filled from inv.customer.phone)
 *   • see / edit the message
 *   • copy the message OR open WhatsApp directly
 *
 * Works on every invoice — accounts staff, admin/editor, and the customer/distributor themselves.
 */
export default function WhatsAppShareButton({
    invoice,
    label = "Send on WhatsApp",
    iconOnly = false,
    variant = "default",       // "default" | "ghost" | "outline"
    size = "sm",
    className = "",
    supportContact = "",
    testId,
}) {
    const [open, setOpen] = useState(false);
    const [phone, setPhone] = useState(invoice?.customer?.phone || "");
    const [message, setMessage] = useState(
        defaultInvoiceMessage({ inv: invoice, support: supportContact })
    );
    const [copied, setCopied] = useState(false);

    if (!invoice) return null;
    const link = buildWhatsAppLink({ phone, message });
    const normalised = normaliseWhatsAppPhone(phone);

    const copyText = async () => {
        try {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            toast.success("Message copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed — long-press to copy");
        }
    };

    const reset = () => {
        setPhone(invoice?.customer?.phone || "");
        setMessage(defaultInvoiceMessage({ inv: invoice, support: supportContact }));
    };

    const btnVariantCls = variant === "ghost"
        ? "text-emerald-700 hover:text-emerald-800 inline-flex items-center text-xs"
        : variant === "outline"
            ? "border border-emerald-600 text-emerald-700 hover:bg-emerald-50 inline-flex items-center text-xs px-2 py-1"
            : "bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center text-xs px-3 py-1.5";

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className={`${btnVariantCls} ${className}`}
                title="Send this invoice on WhatsApp"
                data-testid={testId || `wa-share-${invoice.invoice_number || invoice.id}`}
            >
                <MessageCircle className="w-3.5 h-3.5 mr-1" />
                {!iconOnly && label}
            </button>

            <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); setOpen(o); }}>
                <DialogContent className="rounded-none max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                            <MessageCircle className="w-5 h-5 text-emerald-600" />Send invoice on WhatsApp
                        </DialogTitle>
                        <DialogDescription className="text-xs text-stone-500">
                            Opens WhatsApp Web or app with the message pre-filled. You just tap send.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-2">
                        <div className="bg-stone-50 border border-stone-200 px-3 py-2 text-xs">
                            <div><span className="text-stone-500">Invoice:</span> <span className="font-mono">{invoice.invoice_number}</span></div>
                            <div><span className="text-stone-500">Customer:</span> <span className="font-medium text-slate-900">{invoice.customer?.name}</span></div>
                        </div>

                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Recipient phone (with country code)</Label>
                            <Input
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+91 98765 43210"
                                className="rounded-none border-stone-300 mt-1 font-mono"
                                data-testid="wa-phone"
                            />
                            <p className="text-[10px] text-stone-500 mt-1">
                                {normalised
                                    ? <>Will open WhatsApp chat with <span className="font-mono text-emerald-700">+{normalised}</span>.</>
                                    : <>Empty — WhatsApp will let you pick a contact after clicking Send.</>}
                            </p>
                        </div>

                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Message (editable)</Label>
                            <Textarea
                                rows={9}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="rounded-none border-stone-300 mt-1 font-mono text-xs"
                                data-testid="wa-message"
                            />
                            <p className="text-[10px] text-stone-500 mt-1">
                                The PDF link in the message is public — anyone with the link can open the invoice. Don't share publicly.
                            </p>
                        </div>

                        <div className="bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-900">
                            <strong>How it works:</strong> clicking <em>Open in WhatsApp</em> opens WhatsApp Web/app with the message pre-filled. You just tap <em>send</em> there. No charges, no extra setup — works on mobile and desktop WhatsApp.
                        </div>
                    </div>

                    <DialogFooter className="gap-2 flex-wrap">
                        <Button
                            variant="outline"
                            onClick={copyText}
                            className="rounded-none"
                            data-testid="wa-copy"
                        >
                            {copied ? <><Check className="w-4 h-4 mr-2" />Copied</> : <><Copy className="w-4 h-4 mr-2" />Copy message</>}
                        </Button>
                        <a
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => {
                                toast.success("Opening WhatsApp…");
                                setOpen(false);
                            }}
                            className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center justify-center px-4 py-2 text-sm"
                            data-testid="wa-open"
                        >
                            <ExternalLink className="w-4 h-4 mr-2" />Open in WhatsApp
                        </a>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
